/** Puesta en marcha: estado de la entidad y requisitos (GET /setup) y el paso a operación. */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { SETUP_PRESETS, SETUP_STEPS, type SetupCheck, type SetupStatusView, type SetupStepDef } from '@pqrs/contracts';
import { apiGet, apiPost } from '@/api/client';

export const SETUP_QUERY_KEY = ['setup'] as const;

/** Pregunta también al correo, al almacenamiento y al motor: se vuelve a pedir al volver a la ventana. */
export function useSetupStatus(enabled = true) {
  return useQuery({ queryKey: SETUP_QUERY_KEY, queryFn: () => apiGet<SetupStatusView>('/setup'), enabled, staleTime: 30_000, refetchOnWindowFocus: true });
}

/** «Revisar de nuevo»: pregunta otra vez al correo y al motor (el API reutiliza su respuesta un minuto). */
export function useRecheckSetup() {
  const qc = useQueryClient();
  return useMutation({ mutationFn: () => apiGet<SetupStatusView>('/setup', { fresh: true }), onSuccess: (st) => qc.setQueryData(SETUP_QUERY_KEY, st) });
}

export function useGoLive() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => apiPost<SetupStatusView>('/setup/go-live'),
    // La sesión dice si la entidad está en implementación: se espera la nueva antes de seguir (si no, la ruta vuelve aquí).
    onSuccess: (st) => {
      qc.setQueryData(SETUP_QUERY_KEY, st);
      return qc.invalidateQueries({ queryKey: ['me'] });
    },
  });
}

export type SetupStepView = (typeof SETUP_STEPS)[number] & {
  checks: SetupCheck[];
  /** Todos sus requisitos obligatorios se cumplen. */
  done: boolean;
  missing: number;
  warnings: number;
};

/** Los pasos del asistente con sus requisitos. */
export function setupSteps(checks: SetupCheck[]): SetupStepView[] {
  return SETUP_STEPS.map((step) => {
    const own = checks.filter((c) => c.step === step.key);
    const missing = own.filter((c) => c.level === 'required' && !c.ok).length;
    return { ...step, checks: own, done: missing === 0, missing, warnings: own.filter((c) => c.level === 'recommended' && !c.ok).length };
  });
}

/**
 * Los requisitos obligatorios sin cumplir, agrupados por la pantalla de Configuración que los resuelve. `presetsOnly`:
 * solo lo que la entidad ya traía listo (correo, almacenamiento, motor…), que no son pasos del asistente.
 */
export function setupIssues(checks: SetupCheck[], opts: { presetsOnly?: boolean } = {}) {
  const groups: readonly SetupStepDef[] = opts.presetsOnly ? SETUP_PRESETS : [...SETUP_STEPS, ...SETUP_PRESETS];
  return groups
    .map((g) => ({ ...g, checks: checks.filter((c) => c.step === g.key && c.level === 'required' && !c.ok) }))
    .filter((g) => g.checks.length);
}
export type SetupIssue = ReturnType<typeof setupIssues>[number];

/** El primer paso anterior a `step` que falta por completar: mientras exista, `step` no se abre. */
export function blockedBy(step: SetupStepView, steps: SetupStepView[]): SetupStepView | undefined {
  return steps.slice(0, steps.indexOf(step)).find((s) => !s.done);
}
